import type Plan from '@/classes/Plan'
import { usePresentationLocale } from '@/i18n'
import { isEncryptionError, useEncryptionStore } from '@/stores/encryption'
import { usePlanStore } from '@/stores/plan'
import { captureException } from '@sentry/vue'
import { watch, type Reactive, ref, computed } from 'vue'

export function useConfirmIdentity() {
	const planStore = usePlanStore()
	const encryptionStore = useEncryptionStore()
	const { t } = usePresentationLocale()
	const secretValue = ref('')
	const inProgress = ref(false)
	const formValid = computed(() => validateForm())
	const errorMessage = ref('')

	/** Format a partial SSN so the user doesn't have to type dashes */
	function formatValue(value: string) {
		const formatted = value
			.replace(/^([0-9]{3})-?([0-9]{2})([0-9]{1,4})$/g, '$1-$2-$3')
			.replace(/^([0-9]{3})([0-9]{1,2})$/g, '$1-$2')

		return formatted
	}

	/** Returns true if the form is valid */
	function validateForm() {
		// Remove dashes and check if we have 9 digits
		const digitsOnly = secretValue.value.replace(/-/g, '')
		return digitsOnly.length === 9
	}

	// Update the form with the formatted value
	watch(secretValue, (newValue) => {
		const formatted = formatValue(newValue)
		if (newValue !== formatted) {
			secretValue.value = formatted
		}
	})

	/** When the form submits, try to decrypt the vault */
	async function submitForm(plan: Reactive<Plan>) {
		inProgress.value = true

		try {
			await planStore.decryptVault(plan, secretValue.value)
		} catch (error) {
			console.error(error)
			captureException(error)

			if (isEncryptionError(error)) {
				errorMessage.value = t(`encryption.errors.${error.errorType}`)
			} else if (error instanceof Error) {
				errorMessage.value = error.message
			} else {
				errorMessage.value = 'Unknown error'
			}
		}

		inProgress.value = false
	}

	// Clear PII data and reset encryption status
	async function resetPlanHolderEncryptionForPlan(planHolderID: string) {
		await planStore.updatePlan(planHolderID, {
			birthDate: undefined,
			socialSecurityNumber: '',
			address: '',
			city: '',
			state: '',
			zip: '',
			email: '',
			phone: '',
			secondaryPhone: '',
			ownerIsInsured: true,
			policyOwner: {
				firstName: '',
				lastName: '',
				birthDate: undefined,
				socialSecurityNumber: '',
				address: '',
				city: '',
				state: '',
				phone: '',
				secondaryPhone: '',
				email: '',
			},
			completedSteps: [],
			encryptionStatus: 'unencrypted',
			paymentStep: 'html',
			oapStep: 'html',
			everplansStep: 'html',
			vault: null,
		})

		await encryptionStore.deleteEncryptionRecord(planHolderID)

		const url = new URL(window.location.href)
		url.searchParams.set('planId', planHolderID)
		window.location.href = url.toString()
	}

	return {
		errorMessage,
		secretValue,
		formValid,
		inProgress,
		resetPlanHolderEncryptionForPlan,
		submitForm,
	}
}
